/**
 * Derivative cache in the Origin Private File System (OPFS): private to this site, fast, survives
 * reloads, and needs no permission prompts. The browser may evict it under storage pressure, which
 * is fine because every derivative can be regenerated.
 *
 * Layout: `derivatives/<mediaId>/<kind>-<fingerprint>.<ext>` (+ `.meta.json` sidecar for filmstrips).
 */
import type { FilmstripInfo } from '@ve/core';
import type {
  CachedDerivative,
  DerivativeCache,
  DerivativeKey,
  DerivativeKind,
  DerivativeResult,
} from '../types.ts';

const ROOT = 'derivatives';
const EXTENSIONS: Record<DerivativeKind, string> = {
  thumbnail: 'jpg',
  filmstrip: 'jpg',
  waveform: 'json',
  proxy: 'mp4',
};

/** OPFS directory iteration (not in TypeScript's default DOM lib without `DOM.AsyncIterable`). */
type IterableDirectory = FileSystemDirectoryHandle & { values(): AsyncIterable<FileSystemHandle> };

function safe(part: string): string {
  return part.replace(/[^A-Za-z0-9_-]/g, '_');
}

function fileName(key: DerivativeKey): string {
  return `${key.kind}-${safe(key.fingerprint)}.${EXTENSIONS[key.kind]}`;
}

function isNotFound(err: unknown): boolean {
  return err instanceof DOMException && (err.name === 'NotFoundError' || err.name === 'TypeMismatchError');
}

async function writeFile(dir: FileSystemDirectoryHandle, name: string, data: Blob | string): Promise<File> {
  const handle = await dir.getFileHandle(name, { create: true });
  // Writable streams write to a swap file and replace the target atomically on close(), so readers
  // never see a half-written file; an abort leaves the previous version intact.
  const writable = await handle.createWritable();
  try {
    await writable.write(data);
    await writable.close();
  } catch (err) {
    await writable.abort().catch(() => undefined);
    throw err;
  }
  return handle.getFile();
}

export class OpfsDerivativeCache implements DerivativeCache {
  private rootPromise: Promise<FileSystemDirectoryHandle> | null = null;

  static isSupported(): boolean {
    return typeof navigator !== 'undefined' && typeof navigator.storage?.getDirectory === 'function';
  }

  private root(): Promise<FileSystemDirectoryHandle> {
    this.rootPromise ??= navigator.storage
      .getDirectory()
      .then((opfs) => opfs.getDirectoryHandle(ROOT, { create: true }));
    return this.rootPromise;
  }

  private async mediaDir(mediaId: string, create: boolean): Promise<FileSystemDirectoryHandle | null> {
    try {
      return await (await this.root()).getDirectoryHandle(safe(mediaId), { create });
    } catch (err) {
      if (isNotFound(err)) return null;
      throw err;
    }
  }

  async get(key: DerivativeKey): Promise<CachedDerivative | null> {
    const dir = await this.mediaDir(key.mediaId, false);
    if (!dir) return null;
    try {
      const name = fileName(key);
      const file = await (await dir.getFileHandle(name)).getFile();
      if (key.kind !== 'filmstrip') return { file };
      const meta = await (await dir.getFileHandle(`${name}.meta.json`)).getFile();
      return { file, filmstrip: JSON.parse(await meta.text()) as FilmstripInfo };
    } catch (err) {
      if (isNotFound(err) || err instanceof SyntaxError) return null;
      throw err;
    }
  }

  async put(key: DerivativeKey, result: DerivativeResult): Promise<CachedDerivative> {
    const dir = (await this.mediaDir(key.mediaId, true))!;
    const name = fileName(key);
    const file = await writeFile(dir, name, result.blob);
    if (key.kind === 'filmstrip') {
      if (!result.filmstrip) throw new Error('Filmstrip derivative is missing its layout');
      await writeFile(dir, `${name}.meta.json`, JSON.stringify(result.filmstrip));
    }
    // Drop stale versions of this kind (made from an older version of the source file).
    for await (const entry of (dir as IterableDirectory).values()) {
      if (
        entry.name.startsWith(`${key.kind}-`) &&
        entry.name !== name &&
        entry.name !== `${name}.meta.json`
      ) {
        await dir.removeEntry(entry.name).catch(() => undefined);
      }
    }
    return { file, filmstrip: result.filmstrip };
  }

  async remove(mediaId: string): Promise<void> {
    try {
      await (await this.root()).removeEntry(safe(mediaId), { recursive: true });
    } catch (err) {
      if (!isNotFound(err)) throw err;
    }
  }

  /** Bytes used by cached derivatives (walks the cache directory). */
  async usage(): Promise<number> {
    let total = 0;
    const walk = async (dir: FileSystemDirectoryHandle): Promise<void> => {
      for await (const entry of (dir as IterableDirectory).values()) {
        if (entry.kind === 'file') total += (await (entry as FileSystemFileHandle).getFile()).size;
        else await walk(entry as FileSystemDirectoryHandle);
      }
    };
    await walk(await this.root());
    return total;
  }
}
